// client/src/components/CookieBanner.tsx
"use client";

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Settings, Shield, BarChart3, X } from 'lucide-react';
import { useCookieConsent } from '../hooks/useCookieConsent';

interface CookieCategory {
  key: 'essential' | 'analytics';
  name: string;
  label: string;
  description: string;
  icon: React.ReactNode;
  required: boolean;
}

const AUTO_MINIMIZE_MS = 5000;

const CookieBanner: React.FC = () => {
  const { hasDecided, isLoading, acceptAll, rejectAll, updateConsent, consent } = useCookieConsent();
  const [showCustomize, setShowCustomize] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const minimizeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cookie categories configuration
  const categories: CookieCategory[] = [
    {
      key: 'essential',
      name: 'Essential Cookies',
      label: 'Essential Cookies',
      description: 'Required for the website to function properly, including authentication and security.',
      icon: <Shield className="w-4 h-4" />,
      required: true,
    },
    {
      key: 'analytics',
      name: 'Analytics Cookies',
      label: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website to improve user experience. If you reject them, we only count page visits anonymously, without cookies.',
      icon: <BarChart3 className="w-4 h-4" />,
      required: false,
    },
  ];

  // Show banner only if user hasn't made a decision and loading is complete
  useEffect(() => {
    if (!isLoading && !hasDecided) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsVisible(true);
    } else {

      setIsVisible(false);
    }
  }, [isLoading, hasDecided]);

  // Auto-minimize the full banner after AUTO_MINIMIZE_MS so it stops obscuring
  // page content (e.g. the plug-in solar ROI calculator's verdict card). The
  // minimized pill remains visible, keyboard-reachable, and announces the
  // outstanding consent decision to assistive tech.
  useEffect(() => {
    if (!isVisible || isMinimized || showCustomize) {
      if (minimizeTimerRef.current) {
        clearTimeout(minimizeTimerRef.current);
        minimizeTimerRef.current = null;
      }
      return;
    }
    minimizeTimerRef.current = setTimeout(() => {
      setIsMinimized(true);
      minimizeTimerRef.current = null;
    }, AUTO_MINIMIZE_MS);
    return () => {
      if (minimizeTimerRef.current) {
        clearTimeout(minimizeTimerRef.current);
        minimizeTimerRef.current = null;
      }
    };
  }, [isVisible, isMinimized, showCustomize]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCustomize) {
        setShowCustomize(false);
      }
    };

    if (showCustomize) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [showCustomize]);

  // Clear any pending timer on unmount to avoid leaked timeouts
  useEffect(() => {
    return () => {
      if (minimizeTimerRef.current) clearTimeout(minimizeTimerRef.current);
    };
  }, []);

  // Don't render if banner shouldn't be visible
  if (!isVisible) return null;

  const handleAcceptAll = () => {
    acceptAll();
    setIsVisible(false);
    setIsMinimized(false);
  };

  const handleRejectAll = () => {
    rejectAll();
    setIsVisible(false);
    setIsMinimized(false);
  };

  const handleCustomizeSave = () => {
    setShowCustomize(false);
    setIsVisible(false);
    setIsMinimized(false);
  };

  const handleCustomizeCancel = () => {
    setShowCustomize(false);
  };

  const handleExpand = () => {
    setIsMinimized(false);
  };

  // Animation variants - slide in from the bottom edge.
  const bannerVariants = {
    hidden: {
      opacity: 0,
      y: 100,
      transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] as const }
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] as const }
    },
    exit: {
      opacity: 0,
      y: 100,
      transition: { duration: 0.3, ease: [0.4, 0, 1, 1] as const }
    }
  };

  const pillVariants = {
    hidden: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const }
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] as const }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2, ease: [0.4, 0, 1, 1] as const }
    }
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] as const }
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] as const }
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: { duration: 0.2, ease: [0.4, 0, 1, 1] as const }
    }
  };

  return (
    <>
      {/* Main Cookie Banner - bottom-anchored, auto-minimizes after 5s.
          Top placement was considered but the PublicNavbar (z-50, top-0) on
          the calculator route would be obscured; the bottom placement lets
          the verdict card stay visible above the banner, and the auto-minimize
          ensures the banner stops blocking content after the user has had a
          moment to read it. */}
      <AnimatePresence>
        {!isMinimized && (
          <motion.div
            key="cookie-banner"
            variants={bannerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="fixed bottom-4 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:bottom-10 md:max-w-sm z-[10000] bg-authority-600 border-4 border-white shadow-[8px_8px_0px_0px_rgba(255,255,255,1)]"
            role="dialog"
            aria-modal="false"
            aria-labelledby="cookie-banner-title"
            aria-describedby="cookie-banner-description"
          >
            <div className="p-4 md:p-6">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 mb-3 md:mb-4">
                <div className="flex-1">
                  <h2 id="cookie-banner-title" className="text-base md:text-xl font-bold text-white mb-1 md:mb-2">
                    Cookie Preferences
                  </h2>
                  <p id="cookie-banner-description" className="text-xs md:text-sm text-white leading-relaxed line-clamp-2">
                    We use cookies to enhance your experience. Your privacy matters to us.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-row gap-2">
                <button
                  onClick={handleAcceptAll}
                  className="cursor-pointer flex-1 no-wrap bg-white text-black font-bold py-2.5 md:py-3 px-3 md:px-4 border-2 border-black hover:bg-gray-100 transition-colors duration-200 text-xs md:text-sm"
                  aria-label="Accept all cookies"
                  role="button"
                  style={{ textWrap: "nowrap" }}
                >
                  Accept All
                </button>

                <button
                  onClick={handleRejectAll}
                  className="cursor-pointer flex-1 bg-transparent text-white font-bold py-2.5 md:py-3 px-3 md:px-4 border-2 border-white hover:bg-white hover:text-black transition-colors duration-200 text-xs md:text-sm"
                  aria-label="Reject non-essential cookies"
                  style={{ textWrap: "nowrap" }}
                >
                  Reject All
                </button>

                <button
                  onClick={() => setShowCustomize(true)}
                  className="cursor-pointer flex-1 bg-gray-800 text-white font-bold py-2.5 md:py-3 px-3 md:px-4 border-2 border-gray-600 hover:bg-gray-700 transition-colors duration-200 text-xs md:text-sm flex items-center justify-center gap-1.5 md:gap-2"
                  aria-label="Customise cookie preferences"
                >
                  <Settings className="w-3 h-3 md:w-4 md:h-4" />
                  <span className="hidden sm:inline">Customise</span>
                  <span className="sm:hidden">Options</span>
                </button>
              </div>

              {/* Privacy Policy Link */}
              <div className="mt-3 md:mt-4 text-center hidden md:block">
                <a
                  href="/privacy"
                  className="cursor-pointer text-xs text-white hover:text-gray-200 underline transition-colors duration-200"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Read our Privacy Policy
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Minimized pill - persistent indicator that consent is still pending.
          Bottom-right corner sits well clear of the calculator verdict card
          (top-right sticky on desktop, top of viewport on mobile). */}
      <AnimatePresence>
        {isMinimized && (
          <motion.button
            key="cookie-pill"
            type="button"
            variants={pillVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={handleExpand}
            className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-[10000] flex items-center gap-2 bg-authority-600 border-2 border-white shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] px-3 py-2 text-white font-bold text-xs md:text-sm hover:bg-authority-600/90 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/40"
            aria-label="Cookie preferences - open consent banner"
            aria-expanded="false"
            aria-haspopup="dialog"
            data-testid="cookie-pill"
          >
            <Cookie className="w-4 h-4" aria-hidden="true" />
            <span className="hidden sm:inline">Cookie preferences</span>
            <span className="sm:hidden">Cookies</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Customization Modal */}
      <AnimatePresence>
        {showCustomize && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-[10001]"
              onClick={handleCustomizeCancel}
              aria-hidden="true"
            />

            {/* Modal */}
            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl bg-authority-600 border-4 border-white shadow-[8px_8px_0px_0px_rgba(255,255,255,1)] z-[10002] overflow-auto"
              role="dialog"
              aria-modal="true"
              aria-labelledby="customize-modal-title"
            >
              <div className="p-6">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <h3 id="customize-modal-title" className="text-2xl font-bold text-white">
                    Customize Cookie Preferences
                  </h3>
                  <button
                    onClick={handleCustomizeCancel}
                    className="text-white hover:text-gray-300 transition-colors duration-200 p-1"
                    aria-label="Close customization modal"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                {/* Cookie Categories */}
                <div className="space-y-4 mb-6">
                  {categories.map((category) => (
                    <div
                      key={category.key}
                      className="bg-gray-900/50 border-2 border-gray-700 p-4"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="text-white">
                              {category.icon}
                            </div>
                            <h4 className="text-lg font-semibold text-white">
                              {category.name}
                            </h4>
                            {category.required && (
                              <span className="bg-white text-black text-xs font-bold px-2 py-1">
                                Required
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-300 leading-relaxed">
                            {category.description}
                          </p>
                        </div>

                        {/* Toggle Switch */}
                        <div className="ml-4">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={consent[category.key]}
                              onChange={(e) => updateConsent(category.key, e.target.checked)}
                              disabled={category.required}
                              className="sr-only peer"
                              aria-describedby={`${category.key}-description`}
                              aria-label={`Toggle ${category.label}`}
                            />
                            <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-gray-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-white peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                            <span className="sr-only">{category.label}</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Modal Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    onClick={handleCustomizeSave}
                    className="flex-1 bg-white text-black font-bold py-3 px-4 border-2 border-black hover:bg-gray-100 transition-colors duration-200"
                  >
                    Save Preferences
                  </button>

                  <button
                    onClick={handleCustomizeCancel}
                    className="flex-1 bg-transparent text-white font-bold py-3 px-4 border-2 border-white hover:bg-white hover:text-black transition-colors duration-200"
                  >
                    Cancel
                  </button>
                </div>

                {/* Additional Info */}
                <div className="mt-4 text-center">
                  <p className="text-xs text-white">
                    You can change these preferences at any time in your settings. <a href="/privacy" target="_blank" className="underline">View our privacy policy.</a>
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieBanner;
